import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { BrandLogo } from '@/context/BrandingContext';

const TABS = [
  { key: 'home', label: 'Home', path: '/dashboard' },
  { key: 'actions', label: 'Actions', path: '/actions' },
  { key: 'presence', label: 'Presence', path: '/presence' },
  { key: 'accuracy', label: 'AI Said WHAT?', path: '/accuracy' },
  { key: 'visibility', label: 'Recommendations', path: '/visibility' },
  { key: 'optimize', label: 'Optimize', path: '/optimize' },
  { key: 'directories', label: 'Directories', path: '/directories' },
  { key: 'leads', label: 'Leads', path: '/leads' },
  { key: 'intel', label: 'Intel', path: '/competitors' },
  { key: 'referrals', label: 'Referrals', path: '/referrals' },
  { key: 'content', label: 'Content', path: '/content' },
  { key: 'profile', label: 'Profile', path: '/profile' },
  { key: 'settings', label: 'Settings', path: '/settings' },
];

export default function DesktopSidebar({ score }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('gcf_sidebar_collapsed') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('gcf_sidebar_collapsed', collapsed); } catch {}
  }, [collapsed]);

  const activeKey = TABS.find(t => location.pathname.startsWith(t.path))?.key || 'home';

  return (
    <aside
      className="fixed left-0 top-0 h-screen z-40 flex flex-col transition-[width] duration-200 ease-out"
      style={{
        width: collapsed ? 64 : 220,
        backgroundColor: '#EDE8DF',
        borderRight: '1px solid #D4C9B8',
      }}
      data-testid="desktop-sidebar"
    >
      {/* Logo */}
      <div className="flex items-center px-4 h-14" style={{ borderBottom: '1px solid #D4C9B8' }}>
        {!collapsed ? (
          <BrandLogo
            slot="sidebar"
            className="h-7"
            fallback={<span className="text-[15px] font-semibold" style={{ color: '#3C3226', fontFamily: "'DM Sans', sans-serif" }}>GetChosenFirst</span>}
          />
        ) : (
          <BrandLogo
            slot="sidebar"
            className="h-5 w-5 object-contain"
            fallback={<span className="text-[15px] font-bold" style={{ color: '#3C3226' }}>G</span>}
          />
        )}
      </div>

      {/* Nav items — text only, ALL CAPS, no icons */}
      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto">
        {TABS.map(tab => {
          const isActive = activeKey === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => navigate(tab.path)}
              className="w-full flex items-center transition-all"
              style={{
                padding: collapsed ? '10px 0' : '12px 16px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                color: isActive ? '#3C3226' : '#5C5347',
                fontWeight: isActive ? 600 : 400,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                borderLeft: isActive ? '2px solid #B8985A' : '2px solid transparent',
                borderRadius: 0,
                backgroundColor: 'transparent',
              }}
              data-testid={`sidebar-${tab.key}`}
            >
              {!collapsed && <span>{tab.label}</span>}
              {collapsed && <span className="text-[10px]">{tab.label.charAt(0)}</span>}
            </button>
          );
        })}
      </nav>

      {/* Score badge */}
      {score !== null && score !== undefined && (
        <div className="px-3 pb-3" style={{ borderTop: '1px solid #D4C9B8' }}>
          <div className="flex items-center gap-2 pt-3">
            <div className="relative flex-shrink-0" style={{ width: 32, height: 32 }}>
              <svg width="32" height="32" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="13" fill="none" stroke="#D4C9B8" strokeWidth="3" />
                <circle
                  cx="16" cy="16" r="13" fill="none"
                  stroke="#1D6B4F" strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${(score / 100) * 81.68} 81.68`}
                  transform="rotate(-90 16 16)"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold" style={{ color: '#3C3226' }}>
                {score}
              </span>
            </div>
            {!collapsed && (
              <span className="text-[11px]" style={{ color: '#8C7E6E' }}>Your score</span>
            )}
          </div>
        </div>
      )}

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="flex items-center justify-center h-10 transition-colors"
        style={{ borderTop: '1px solid #D4C9B8' }}
        data-testid="sidebar-collapse-toggle"
      >
        {collapsed ? <ChevronRight size={14} color="#8C7E6E" /> : <ChevronLeft size={14} color="#8C7E6E" />}
      </button>
    </aside>
  );
}
